import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTelegramPlane,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaRedditAlien,
  FaPinterestP,
} from "react-icons/fa";
import apiClient from "../../Services/apiInterceptor";
import { socialPostService } from "../../Services/socialPostService";
import { path_url } from "../../Config/config";
import MainSidebar from "../Mainsidebar";
import UserNavbar from "../UserNavbar";

/* ─── helpers ────────────────────────────────────────────── */
const getUserId = () => localStorage.getItem("userId") || "";

/**
 * Normalise any API response shape into a plain config object.
 * Handles: { statusCode, body: [...] }, plain arrays, plain objects.
 */
const getConfigObj = (data) => {
  if (!data) return null;
  let d = data;
  if (data.statusCode && Array.isArray(data.body)) d = data.body;
  if (data.statusCode === "OK" && (!data.body || data.body.length === 0))
    return null;
  if (Array.isArray(d)) return d.length > 0 ? d[0] : null;
  if (typeof d === "object" && Object.keys(d).length === 0) return null;
  return d;
};

/**
 * Validate that a platform config has the minimum required fields.
 * Facebook: only pageId + pageAccessToken + isActive (pageName/category are optional).
 */
const isValidConfig = (platform, data) => {
  const c = getConfigObj(data);
  if (!c) return false;

  const isActive = c.isActive === true || c.active === true;
  const p = platform.toUpperCase();

  if (p === "TELEGRAM") {
    return !!(c.botToken && c.chatId && isActive);
  }
  if (p === "FACEBOOK") {
    // pageName and category are NOT required — they may be null if backend enrichment is pending
    return !!(c.pageId && c.pageAccessToken && isActive);
  }
  if (p === "INSTAGRAM") {
    const igId = c.instagramBusinessAccountId || c.instagramAccountId;
    const token = c.accessToken || c.pageAccessToken;
    return !!(igId && token && isActive);
  }
  if (p === "LINKEDIN") {
    const clientId = c.appClientID || c.clientId;
    const clientSecret = c.appClientSecret || c.clientSecret;
    const token = c.appAuthorizedToken || c.accessToken;
    return !!(clientId && clientSecret && token);
  }
  return false;
};

/**
 * Fetch config for a single platform. Results are NOT cached here —
 * the component manages a configCache ref to avoid duplicate network calls.
 */
const fetchPlatformConfig = async (platform) => {
  const usercid = getUserId();
  if (!usercid) return null;
  try {
    let res;
    if (platform === "TELEGRAM")
      res = await apiClient.get(`/telegram/${usercid}`);
    if (platform === "FACEBOOK")
      res = await apiClient.get(`/facebook/userid/${usercid}`);
    if (platform === "INSTAGRAM")
      res = await apiClient.get(`/instagram/user/${usercid}`);
    if (platform === "LINKEDIN")
      res = await apiClient.get(`/linkedin/userid/${usercid}`);
    return res?.data || null;
  } catch (err) {
    // 404 / 500 means no config saved yet — treat as not connected
    return null;
  }
};

const C = {
  blue: "#3B82F6",
  blueDeep: "#2563eb",
  blueSoft: "#EAF6FF",
  blueMid: "#D6EEFF",
  blueLight: "#A7D8FF",
  white: "#ffffff",
  text: "#1e3a5f",
  textSub: "#4b6e91",
  textMuted: "#93b5d0",
  border: "#D6EEFF",
  error: "#dc2626",
  success: "#16a34a",
  warning: "#d97706",
};

const PLATFORMS = [
  {
    id: "TELEGRAM",
    label: "Telegram",
    icon: <FaTelegramPlane />,
    color: "#26a5e4",
    bg: "#e8f7ff",
  },
  {
    id: "FACEBOOK",
    label: "Facebook",
    icon: <FaFacebookF />,
    color: "#1877f2",
    bg: "#e7f0ff",
  },
  {
    id: "INSTAGRAM",
    label: "Instagram",
    icon: <FaInstagram />,
    color: "#e4405f",
    bg: "#fff0f3",
  },
  {
    id: "LINKEDIN",
    label: "LinkedIn",
    icon: <FaLinkedinIn />,
    color: "#0a66c2",
    bg: "#e8f1ff",
  },
  {
    id: "WHATSAPP",
    label: "WhatsApp",
    icon: <FaWhatsapp />,
    color: "#25D366",
    bg: "#e6f9f0",
  },
  {
    id: "REDDIT",
    label: "Reddit",
    icon: <FaRedditAlien />,
    color: "#FF4500",
    bg: "#fff0e5",
  },
  {
    id: "PINTEREST",
    label: "Pinterest",
    icon: <FaPinterestP />,
    color: "#E60023",
    bg: "#ffe6e9",
  },
];

/**
 * Publish helpers
 * IMPORTANT: facebookId here is the backend DB record UUID (c.id), NOT the Facebook pageId.
 * The backend uses this UUID to look up the stored pageId + pageAccessToken from the database.
 */
const publishToTelegram = async (message, mediaFile, mediaUrl, mediaType) => {
  const usercid = getUserId();
  const isImage =
    mediaType?.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl || mediaFile?.name || "");
  const isVideo =
    mediaType?.startsWith("video/") ||
    /\.(mp4|mov|avi|webm)$/i.test(mediaUrl || mediaFile?.name || "");
  const isDocument =
    !isImage &&
    !isVideo &&
    (mediaFile ||
      /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|zip|rar)$/i.test(
        mediaUrl || mediaFile?.name || ""
      ));

  if (mediaFile) {
    const formData = new FormData();
    const endpoint = isImage
      ? "/telegram/send-photo"
      : isVideo
        ? "/telegram/send-video"
        : isDocument
          ? "/telegram/send-document"
          : "/telegram/send-message";
    if (isImage || isVideo || isDocument) formData.append("file", mediaFile);
    return apiClient.post(endpoint, isImage || isVideo || isDocument ? formData : null, {
      params: {
        usercid,
        ...(isImage || isVideo || isDocument
          ? (message ? { caption: message } : {})
          : { message: message || "" }),
      },
      headers:
        isImage || isVideo || isDocument ? { "Content-Type": "multipart/form-data" } : {},
    });
  } else if (mediaUrl && isImage) {
    return apiClient.post("/telegram/send-photo", null, {
      params: { usercid, photoUrl: mediaUrl, ...(message ? { caption: message } : {}) },
    });
  } else if (mediaUrl && isVideo) {
    return apiClient.post("/telegram/send-video", null, {
      params: { usercid, videoUrl: mediaUrl, ...(message ? { caption: message } : {}) },
    });
  } else if (mediaUrl && isDocument) {
    return apiClient.post("/telegram/send-document", null, {
      params: { usercid, documentUrl: mediaUrl, ...(message ? { caption: message } : {}) },
    });
  } else {
    return apiClient.post("/telegram/send-message", null, {
      params: { usercid, message: message || "" },
    });
  }
};

/**
 * publishToFacebook
 * @param {string} facebookRecordId - The backend DB UUID (c.id from /facebook/userid/{usercid})
 *   This is NOT the Facebook pageId. The backend service uses this ID to fetch the
 *   stored pageId and pageAccessToken from the database to make the Graph API call.
 */
const publishToFacebook = async (
  facebookRecordId,
  message,
  mediaFile,
  mediaUrl,
  mediaType,
) => {
  const isImage =
    mediaType?.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl || "");
  const isVideo =
    mediaType?.startsWith("video/") ||
    /\.(mp4|mov|avi|webm)$/i.test(mediaUrl || "");
  // Send the DB record UUID under the field name the backend expects
  const baseParams = {
    id: facebookRecordId,
    facebookConfigId: facebookRecordId,
    facebookId: facebookRecordId,
  };
  if (mediaFile) {
    const fd = new FormData();
    fd.append("file", mediaFile);
    const endpoint = isImage
      ? "/facebook/image-post"
      : isVideo
        ? "/facebook/video-post"
        : "/facebook/post";
    return apiClient.post(endpoint, fd, {
      params: {
        ...baseParams,
        ...(isImage
          ? { caption: message }
          : isVideo
            ? { description: message }
            : { message }),
      },
      headers: { "Content-Type": "multipart/form-data" },
    });
  } else if (mediaUrl && isImage) {
    return apiClient.post("/facebook/image-post", null, {
      params: { ...baseParams, imageUrl: mediaUrl, caption: message },
    });
  } else {
    return apiClient.post("/facebook/post", null, {
      params: { ...baseParams, message },
    });
  }
};

const publishToInstagram = async (
  instagramId,
  message,
  mediaFile,
  mediaUrl,
  mediaType,
) => {
  const isVideo =
    mediaType?.startsWith("video/") ||
    /\.(mp4|mov|avi|webm)$/i.test(mediaUrl || "");
  if (!mediaFile && !mediaUrl)
    throw new Error("Instagram requires a media file or URL");
  const endpoint = `/instagram/${instagramId}/posts/${isVideo ? "video" : "image"}`;
  if (mediaFile) {
    const fd = new FormData();
    fd.append("file", mediaFile);
    return apiClient.post(endpoint, fd, {
      params: { caption: message },
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  return apiClient.post(endpoint, null, {
    params: {
      ...(isVideo ? { videoUrl: mediaUrl } : { imageUrl: mediaUrl }),
      caption: message,
    },
  });
};

/* ─── inline styles ──────────────────────────────────────── */
const s = {
  inner: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "16px",
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "20px",
  },
  headerTitle: {
    fontSize: "1.8rem",
    fontWeight: 800,
    color: C.text,
    margin: 0,
  },
  headerSubtitle: {
    color: C.textSub,
    fontSize: "0.92rem",
    margin: "6px 0 0 0",
  },
  headerActions: { display: "flex", gap: "10px", flexWrap: "wrap" },
  actionBtn: {
    background: C.white,
    border: `1.5px solid ${C.blueLight}`,
    color: C.blue,
    borderRadius: "10px",
    padding: "8px 18px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  },
  dashboardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "24px",
    alignItems: "start",
  },
  leftCol: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    flex: "2 1 60%",
  },
  rightCol: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    flex: "1 1 35%",
  },
  card: {
    background: C.white,
    borderRadius: "12px",
    boxShadow: "0 4px 18px rgba(59,130,246,0.06)",
    border: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  cardHead: {
    borderBottom: `1px solid ${C.border}`,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: C.blueSoft,
  },
  cardHeadTitle: { fontWeight: 700, fontSize: "0.95rem", color: C.text },
  cardBody: { padding: "20px" },
  textarea: {
    width: "100%",
    minHeight: "120px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    fontSize: "0.95rem",
    color: C.text,
    background: "#fafcff",
    resize: "vertical",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  textareaErr: { borderColor: C.error, background: "#fef2f2" },
  charCounter: {
    textAlign: "right",
    fontSize: "0.78rem",
    color: C.textMuted,
    marginTop: "4px",
  },
  label: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: C.text,
    display: "block",
    marginBottom: "8px",
  },
  labelReq: { color: C.error },
  mediaToggle: { display: "flex", gap: "8px", marginBottom: "14px" },
  mediaTabBtn: (active) => ({
    padding: "7px 16px",
    border: `1.5px solid ${active ? C.blue : C.border}`,
    borderRadius: "8px",
    background: active ? C.blueSoft : C.white,
    color: active ? C.blue : C.textSub,
    fontWeight: 600,
    fontSize: "0.84rem",
    cursor: "pointer",
  }),
  fileInput: { display: "none" },
  dragDropArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    border: `2px dashed ${C.blueLight}`,
    borderRadius: "10px",
    padding: "30px 20px",
    background: "#fafcff",
    cursor: "pointer",
    textAlign: "center",
    color: C.textSub,
    transition: "background 0.2s",
    ":hover": { background: C.blueSoft },
  },
  urlInput: {
    width: "100%",
    padding: "11px 14px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "10px",
    fontSize: "0.92rem",
    color: C.text,
    background: "#fafcff",
    outline: "none",
    boxSizing: "border-box",
  },
  preview: {
    marginTop: "14px",
    borderRadius: "10px",
    overflow: "hidden",
    padding: "10px",
    background: C.blueSoft,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    position: "relative",
  },
  removeMediaBtn: {
    background: C.error,
    color: C.white,
    border: "none",
    borderRadius: "6px",
    padding: "4px 10px",
    fontSize: "0.75rem",
    cursor: "pointer",
    fontWeight: 600,
  },
  platformGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: "12px",
  },
  platformCard: (selected, color, bg) => ({
    border: `2px solid ${selected ? color : C.border}`,
    borderRadius: "10px",
    padding: "14px 10px",
    cursor: "pointer",
    background: selected ? bg : C.white,
    transition: "all 0.18s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    position: "relative",
    minHeight: "110px",
    justifyContent: "center",
  }),
  platformIcon: { fontSize: "1.6rem" },
  platformName: (selected, color) => ({
    fontSize: "0.85rem",
    fontWeight: 700,
    color: selected ? color : C.textSub,
  }),
  checkMark: {
    position: "absolute",
    top: "6px",
    right: "8px",
    fontSize: "0.75rem",
    color: C.success,
  },
  connBadge: (ok) => ({
    fontSize: "0.65rem",
    fontWeight: 600,
    color: ok ? C.success : C.textMuted,
    background: ok ? "#f0fdf4" : "#f1f5f9",
    border: `1px solid ${ok ? "#bbf7d0" : "#e2e8f0"}`,
    borderRadius: "20px",
    padding: "2px 6px",
  }),
  publishBtn: (disabled) => ({
    width: "100%",
    padding: "14px",
    background: disabled
      ? "#94a3b8"
      : `linear-gradient(135deg, ${C.blue}, ${C.blueDeep})`,
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "0.95rem",
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "0 4px 14px rgba(59,130,246,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "all 0.2s",
    marginTop: "12px",
  }),
  resultsWrap: { display: "flex", flexDirection: "column", gap: "10px" },
  resultRow: (ok) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    background: ok ? "#f0fdf4" : "#fef2f2",
    border: `1px solid ${ok ? "#bbf7d0" : "#fecaca"}`,
    borderRadius: "10px",
    padding: "12px 16px",
  }),
  resultIcon: { fontSize: "1.2rem" },
  resultTitle: (ok) => ({
    fontWeight: 700,
    fontSize: "0.93rem",
    color: ok ? C.success : C.error,
  }),
  resultMsg: { fontSize: "0.82rem", color: C.textSub, marginTop: "2px" },
  missingBanner: {
    background: "#fffbeb",
    border: `1px solid #fde68a`,
    borderRadius: "10px",
    padding: "14px 18px",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "16px",
  },
  missingIcon: { fontSize: "1.2rem", flexShrink: 0 },
  missingText: { flex: 1 },
  missingTitle: {
    fontWeight: 700,
    color: "#92400e",
    fontSize: "0.88rem",
    marginBottom: "4px",
  },
  settingsLink: {
    background: "#fbbf24",
    color: "#1c1917",
    border: "none",
    borderRadius: "6px",
    padding: "6px 14px",
    fontWeight: 700,
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  analyticsGrid: { display: "flex", flexDirection: "column", gap: "16px" },
  analyticsStat: {
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "12px",
    ":last-child": { borderBottom: "none" },
  },
  statVal: { fontSize: "1.6rem", fontWeight: 800, color: C.text },
  statLabel: { fontSize: "0.8rem", color: C.textSub, marginTop: "2px" },
  statTrend: { fontSize: "0.75rem", color: C.success, marginTop: "4px" },
  widgetList: { display: "flex", flexDirection: "column", gap: "14px" },
  widgetItem: {
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  widgetItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  widgetItemTime: { fontSize: "0.75rem", color: C.textMuted },
  widgetItemStatus: {
    fontSize: "0.75rem",
    color: C.blue,
    fontWeight: 600,
    background: C.blueSoft,
    padding: "2px 8px",
    borderRadius: "12px",
  },
  widgetItemTitle: { fontSize: "0.88rem", fontWeight: 600, color: C.text },
  widgetItemFooter: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.8rem",
    color: C.textSub,
    marginTop: "4px",
  },
  recentStatusBadge: (published) => ({
    fontSize: "0.72rem",
    color: published ? C.success : C.error,
    fontWeight: 600,
    background: published ? "#f0fdf4" : "#fef2f2",
    padding: "2px 8px",
    borderRadius: "12px",
    border: `1px solid ${published ? "#bbf7d0" : "#fecaca"}`,
  }),
  toast: (type) => ({
    position: "fixed",
    bottom: "28px",
    right: "28px",
    zIndex: 9999,
    background:
      type === "success" ? C.success : type === "error" ? C.error : C.blue,
    color: "#fff",
    borderRadius: "10px",
    padding: "12px 20px",
    fontWeight: 600,
    fontSize: "0.9rem",
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    maxWidth: "350px",
  }),

  // Social Hub styles
  bottomTabs: {
    display: "flex",
    gap: "8px",
    borderBottom: `2px solid ${C.border}`,
    marginBottom: "20px",
    paddingBottom: "1px",
  },
  bottomTabBtn: (active) => ({
    padding: "10px 20px",
    border: "none",
    borderBottom: active ? `3px solid ${C.blue}` : "3px solid transparent",
    background: "none",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.95rem",
    color: active ? C.blue : C.textSub,
    transition: "all 0.15s",
  }),

  // Table Styling
  tableWrap: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: "0.9rem",
  },
  th: {
    padding: "12px 16px",
    background: "#f8fafc",
    color: C.textSub,
    fontWeight: 700,
    borderBottom: `2px solid ${C.border}`,
    fontSize: "0.82rem",
    textTransform: "uppercase",
  },
  td: {
    padding: "14px 16px",
    borderBottom: `1px solid ${C.border}`,
    color: C.text,
    verticalAlign: "middle",
  },
  trHover: {
    transition: "background 0.15s",
    ":hover": { background: C.blueSoft },
  },

  platformBadge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: `${color}15`,
    color: color,
    padding: "4px 10px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.8rem",
  }),
  typeBadge: (type) => ({
    display: "inline-block",
    background: "#f1f5f9",
    color: C.textSub,
    padding: "2px 8px",
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "0.75rem",
  }),
  statusBadge: {
    display: "inline-block",
    background: "#e0f2fe",
    color: C.blue,
    padding: "2px 8px",
    borderRadius: "12px",
    fontWeight: 600,
    fontSize: "0.75rem",
    border: `1px solid ${C.blueLight}`,
  },

  btnView: {
    background: C.blueSoft,
    color: C.blue,
    border: `1px solid ${C.blueLight}`,
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    marginRight: "8px",
    transition: "all 0.15s",
  },
  btnDelete: {
    background: "#fef2f2",
    color: C.error,
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    transition: "all 0.15s",
  },
  btnEdit: {
    background: "#f0fdf4",
    color: C.success,
    border: "1px solid #bbf7d0",
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    marginRight: "8px",
    transition: "all 0.15s",
  },
  btnPublish: {
    background: C.blueSoft,
    color: C.success,
    border: `1px solid #bae6fd`,
    borderRadius: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.8rem",
    transition: "all 0.15s",
  },

  filterBar: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
    background: "#f8fafc",
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${C.border}`,
  },
  filterField: { display: "flex", flexDirection: "column", gap: "6px" },
  filterLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: C.textSub,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: C.text,
    outline: "none",
    background: "#ffffff",
  },
  select: {
    width: "100%",
    padding: "8px 12px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: C.text,
    outline: "none",
    background: "#ffffff",
    cursor: "pointer",
  },
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: C.textSub,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  pagBtn: (disabled) => ({
    background: disabled ? "#f1f5f9" : "#ffffff",
    border: `1.5px solid ${C.border}`,
    color: disabled ? C.textMuted : C.blue,
    padding: "6px 14px",
    borderRadius: "6px",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
  }),
  pagInfo: { fontSize: "0.85rem", color: C.textSub },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(30,58,95,0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: "20px",
  },
  modalCard: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "600px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
  },
  modalHead: {
    background: C.blueSoft,
    borderBottom: `1px solid ${C.border}`,
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { fontSize: "1.1rem", fontWeight: 800, color: C.text, margin: 0 },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: "1.2rem",
    cursor: "pointer",
    color: C.textSub,
  },
  modalBody: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "75vh",
    overflowY: "auto",
  },
  modalFooter: {
    borderTop: `1px solid ${C.border}`,
    padding: "16px 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    background: "#f8fafc",
  },
  detailRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    borderBottom: "1px solid #f8fafc",
    paddingBottom: "12px",
  },
  detailLabel: { fontWeight: 700, color: C.textSub, fontSize: "0.85rem" },
  detailValue: { color: C.text, fontSize: "0.9rem", wordBreak: "break-all" },
  mediaPreview: {
    background: C.blueSoft,
    borderRadius: "8px",
    padding: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxHeight: "250px",
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    marginTop: "8px",
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function EnteliSocialDashboard() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [messageErr, setMessageErr] = useState(false);
  const [mediaMode, setMediaMode] = useState("none");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [selected, setSelected] = useState([]);
  // configStatus: { TELEGRAM: true/false, FACEBOOK: true/false, ... }
  const [configStatus, setConfigStatus] = useState({});
  // loadingPlatform: which platform card is currently checking connection
  const [loadingPlatform, setLoadingPlatform] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);
  const [missingPlatforms, setMissingPlatforms] = useState([]);
  const [toast, setToast] = useState(null);
  const fileRef = useRef();
  const isMounted = useRef(true);
  // Cache fetched configs so publish does not need to re-fetch them
  const configCache = useRef({});

  // Drafts & Published Posts states
  const [draftsList, setDraftsList] = useState([]);
  const [activeBottomTab, setActiveBottomTab] = useState("drafts");
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginPopupPlatform, setLoginPopupPlatform] = useState("");

  // Filtering states for Published Posts
  const [platformFilter, setPlatformFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // View Modal State
  const [activePost, setActivePost] = useState(null);
  const [fetchingDetail, setFetchingDetail] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);

  // Layout states
  const [hamburger, sethamburger] = useState(false);
  const [isRightMenu, setisRightMenu] = useState(false);
  const [isSearchShow, setIsSearchShow] = useState(false);
  const [fullScreen, setFullScreen] = useState(true);

  // Widget Mock States
  const [analyticsData, setAnalyticsData] = useState({
    postsCount: 28,
    reachCount: 14205,
    engagementRate: 4.8,
  });

  const [scheduledPosts, setScheduledPosts] = useState([
    {
      id: 1,
      title: "Weekly Product Update & Announcements",
      time: "Tomorrow, 10:00 AM",
      platforms: ["FACEBOOK", "LINKEDIN"],
    },
    {
      id: 2,
      title: "Customer Success Stories Video Release",
      time: "Jun 18, 04:30 PM",
      platforms: ["TELEGRAM", "INSTAGRAM"],
    },
  ]);

  const [recentPosts, setRecentPosts] = useState([
    {
      id: 1,
      message:
        "Exciting news! We are launching our redesigned Enteli Social integration settings today! Check it out.",
      time: "2 hours ago",
      status: "Published",
      platforms: ["TELEGRAM", "FACEBOOK"],
    },
    {
      id: 2,
      message:
        "Join our live webinar session this Friday to learn marketing automation best practices.",
      time: "Yesterday",
      status: "Published",
      platforms: ["LINKEDIN"],
    },
    {
      id: 3,
      message: "Check out our new design system tokens!",
      time: "3 days ago",
      status: "Published",
      platforms: ["INSTAGRAM"],
    },
  ]);

  // Layout methods
  const handleScreenEnter = () => {
    setFullScreen(false);
    document.body.requestFullscreen();
  };
  const handleScreenExit = () => {
    setFullScreen(true);
    document.exitFullscreen();
  };
  const handelShow = () => setIsSearchShow(!isSearchShow);
  const handelRightMenuShow = () => setisRightMenu(!isRightMenu);

  const handleclickonbutton = (hamburger) => {
    if (document.documentElement.clientWidth <= 800)
      return { width: "100%", left: "0rem" };
    else
      return {
        width: !hamburger && "-webkit-fill-available",
        left: !hamburger && "3.7rem",
      };
  };

  const handleclickonbutton1 = (hamburger) => {
    if (document.documentElement.clientWidth <= 800) {
      return { display: hamburger && "none" };
    }
  };

  useEffect(() => {
    isMounted.current = true;
    loadDrafts();
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (activeBottomTab === "posts") {
      fetchPostsFromBackend();
    }
  }, [activeBottomTab]);

  const getMediaAbsoluteUrl = (url) => {
    if (!url) return "";
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("data:")
    ) {
      return url;
    }
    return `${path_url}/ticket/file/00000000-0000-0000-0000-000000000000/${url}`;
  };

  const loadDrafts = () => {
    const drafts = JSON.parse(localStorage.getItem("drafts")) || [];
    setDraftsList(drafts);
  };

  const saveDraft = () => {
    if (!message.trim() && selected.length === 0) {
      showToast("Cannot save empty draft.", "error");
      return;
    }
    const existingDrafts = JSON.parse(localStorage.getItem("drafts")) || [];
    const newDraft = {
      id: Date.now(),
      message,
      mediaMode,
      mediaUrl,
      platforms: selected,
      createdAt: new Date().toISOString(),
    };
    const updatedDrafts = [...existingDrafts, newDraft];
    localStorage.setItem("drafts", JSON.stringify(updatedDrafts));
    showToast("Draft saved successfully!", "success");
    loadDrafts();
  };

  const handleEditDraft = (draft) => {
    setMessage(draft.message);
    setMediaMode(draft.mediaMode);
    setMediaUrl(draft.mediaUrl);
    setMediaPreviewUrl(draft.mediaUrl);
    setSelected(draft.platforms);
    showToast("Draft loaded into composer!", "success");
  };

  const handleDeleteDraft = (id) => {
    if (window.confirm("Are you sure you want to delete this draft?")) {
      const drafts = JSON.parse(localStorage.getItem("drafts")) || [];
      const updated = drafts.filter((d) => d.id !== id);
      localStorage.setItem("drafts", JSON.stringify(updated));
      loadDrafts();
      showToast("Draft deleted successfully.", "success");
    }
  };

  const handlePublishDraft = async (draft) => {
    if (!draft.platforms || draft.platforms.length === 0) {
      showToast(
        "Draft has no selected platforms. Please load & configure first.",
        "error",
      );
      return;
    }

    const missing = [];
    for (const platformId of draft.platforms) {
      const cfg = await getOrFetchConfig(platformId);
      const isConn = isValidConfig(platformId, cfg);
      if (!isConn) {
        missing.push(platformId);
      }
    }

    if (missing.length > 0) {
      showToast(
        `Please connect: ${missing.join(", ")} before publishing.`,
        "error",
      );
      return;
    }

    setPublishing(true);
    setResults(null);
    const res = {};
    let anySuccess = false;

    for (const platformId of draft.platforms) {
      try {
        const cfg = await getOrFetchConfig(platformId);
        const c = getConfigObj(cfg);

        if (platformId === "TELEGRAM") {
          await publishToTelegram(draft.message, null, draft.mediaUrl, null);
          res[platformId] = {
            ok: true,
            msg: "Published successfully to Telegram!",
          };
          anySuccess = true;
        } else if (platformId === "FACEBOOK") {
          const facebookRecordId = c?.id;
          if (!facebookRecordId) {
            throw new Error("Facebook record ID (DB UUID) is missing.");
          }
          await publishToFacebook(
            facebookRecordId,
            draft.message,
            null,
            draft.mediaUrl,
            null,
          );
          res[platformId] = { ok: true, msg: `Published to Facebook Page!` };
          anySuccess = true;
        } else if (platformId === "INSTAGRAM") {
          const igId = c?.instagramBusinessAccountId || c?.instagramAccountId;
          if (!igId)
            throw new Error("Instagram Business Account ID not found.");
          await publishToInstagram(
            igId,
            draft.message,
            null,
            draft.mediaUrl,
            null,
          );
          res[platformId] = {
            ok: true,
            msg: "Published successfully to Instagram!",
          };
          anySuccess = true;
        } else if (platformId === "LINKEDIN") {
          res[platformId] = {
            ok: false,
            msg: "LinkedIn post publishing is not yet implemented.",
          };
        }
      } catch (err) {
        const errMsg =
          err?.response?.data?.message ||
          err?.message ||
          `Failed to publish to ${platformId}`;
        res[platformId] = { ok: false, msg: errMsg };
      }
    }

    setPublishing(false);
    setResults(res);

    const allOk = Object.values(res).every((r) => r.ok);

    if (anySuccess) {
      const drafts = JSON.parse(localStorage.getItem("drafts")) || [];
      const updated = drafts.filter((d) => d.id !== draft.id);
      localStorage.setItem("drafts", JSON.stringify(updated));
      loadDrafts();
      showToast("Draft published successfully!", "success");

      const newPost = {
        id: Date.now(),
        message: draft.message,
        time: "Just now",
        status: allOk ? "Published" : "Partially Published",
        platforms: draft.platforms,
      };
      setRecentPosts((prev) => [newPost, ...prev]);
    } else {
      showToast("❌ Draft publication failed.", "error");
    }
  };

  const openLoginPopup = (platform) => {
    setLoginPopupPlatform(platform);
    setShowLoginPopup(true);
  };

  const fetchPostsFromBackend = async () => {
    setLoadingPosts(true);
    try {
      const usercid = getUserId();
      if (!usercid) return;

      let facebookId = null;
      try {
        const fbRes = await apiClient.get(`/facebook/userid/${usercid}`);
        const fbConfig = getConfigObj(fbRes?.data);
        facebookId = fbConfig?.id;
      } catch (err) { }

      const promises = [];
      promises.push(
        socialPostService
          .getPosts("telegram", usercid)
          .then((data) => {
            const list = Array.isArray(data) ? data : data?.body || [];
            return list.map((item) => ({
              ...item,
              platform: "telegram",
              id: item.id || item.postId,
              message:
                item.message ||
                item.caption ||
                item.text ||
                item.description ||
                item.content ||
                "",
              postType:
                item.postType ||
                item.mediaType ||
                (item.mediaUrl ||
                  item.attachmentUrl ||
                  item.imageUrl ||
                  item.photoUrl ||
                  item.videoUrl ||
                  item.fileUrl
                  ? "IMAGE"
                  : "TEXT"),
              mediaUrl: getMediaAbsoluteUrl(
                item.mediaUrl ||
                item.attachmentUrl ||
                item.imageUrl ||
                item.photoUrl ||
                item.videoUrl ||
                item.fileUrl ||
                "",
              ),
              publishedDate:
                item.publishedDate || item.createdAt || item.date || "",
            }));
          })
          .catch((err) => {
            console.error(`Error loading posts for telegram:`, err);
            return [];
          }),
      );

      if (facebookId) {
        promises.push(
          socialPostService
            .getPosts("facebook", facebookId)
            .then((data) => {
              const list = Array.isArray(data) ? data : data?.body || [];
              return list.map((item) => ({
                ...item,
                platform: "facebook",
                id: item.id || item.postId,
                message:
                  item.message ||
                  item.caption ||
                  item.text ||
                  item.description ||
                  item.content ||
                  "",
                postType:
                  item.postType ||
                  item.mediaType ||
                  (item.mediaUrl ||
                    item.attachmentUrl ||
                    item.imageUrl ||
                    item.photoUrl ||
                    item.videoUrl ||
                    item.fileUrl
                    ? "IMAGE"
                    : "TEXT"),
                mediaUrl: getMediaAbsoluteUrl(
                  item.mediaUrl ||
                  item.attachmentUrl ||
                  item.imageUrl ||
                  item.photoUrl ||
                  item.videoUrl ||
                  item.fileUrl ||
                  "",
                ),
                publishedDate:
                  item.publishedDate || item.createdAt || item.date || "",
              }));
            })
            .catch((err) => {
              console.error(`Error loading posts for facebook:`, err);
              return [];
            }),
        );
      }

      const results = await Promise.all(promises);
      const combined = results.flat();
      combined.sort(
        (a, b) =>
          new Date(b.publishedDate || 0) - new Date(a.publishedDate || 0),
      );

      if (isMounted.current) {
        setPosts(combined);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error loading posts:", err);
      if (isMounted.current) {
        setPosts([]);
      }
    } finally {
      if (isMounted.current) {
        setLoadingPosts(false);
      }
    }
  };

  const handleConfirmDeletePost = async (postItem) => {
    if (
      window.confirm(
        "Are you sure you want to delete this post from backend history?",
      )
    ) {
      try {
        await socialPostService.deletePost(postItem.platform, postItem.id);
        showToast("Post deleted successfully.", "success");
        fetchPostsFromBackend();
      } catch (err) {
        console.error("Error deleting post:", err);
        showToast("Delete failed.", "error");
      }
    }
  };

  const handleOpenViewModal = async (platform, id) => {
    setFetchingDetail(true);
    setShowViewModal(true);
    setIsZoomed(false);
    setCopied(false);
    setImgError(false);
    setMediaLoading(true);
    try {
      const detail = await socialPostService.getPost(platform, id);
      if (isMounted.current) {
        const parsedMediaUrl = getMediaAbsoluteUrl(
          detail?.mediaUrl ||
          detail?.attachmentUrl ||
          detail?.imageUrl ||
          detail?.photoUrl ||
          detail?.videoUrl ||
          detail?.fileUrl ||
          "",
        );
        setActivePost({
          platform,
          id: detail?.id || id,
          message:
            detail?.message ||
            detail?.caption ||
            detail?.text ||
            detail?.description ||
            detail?.content ||
            "",
          postType:
            detail?.postType ||
            detail?.mediaType ||
            (parsedMediaUrl ? "IMAGE" : "TEXT"),
          mediaUrl: parsedMediaUrl,
          publishedDate:
            detail?.publishedDate || detail?.createdAt || detail?.date || "",
        });
        if (!parsedMediaUrl) {
          setMediaLoading(false);
        }
      }
    } catch (err) {
      console.error("Error fetching post detail:", err);
      showToast("Error fetching complete post details.", "error");
      setShowViewModal(false);
    } finally {
      if (isMounted.current) {
        setFetchingDetail(false);
      }
    }
  };

  const getFilteredPosts = () => {
    let result = [...posts];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.message?.toLowerCase().includes(q) ||
          p.id?.toString().toLowerCase().includes(q),
      );
    }

    if (platformFilter !== "all") {
      result = result.filter((p) => p.platform === platformFilter);
    }

    if (typeFilter !== "all") {
      result = result.filter(
        (p) => p.postType?.toLowerCase() === typeFilter.toLowerCase(),
      );
    }

    if (fromDate) {
      const from = new Date(fromDate);
      result = result.filter((p) => new Date(p.publishedDate) >= from);
    }
    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      result = result.filter((p) => new Date(p.publishedDate) <= to);
    }

    return result;
  };

  const showToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => {
      if (isMounted.current) setToast(null);
    }, 4000);
  };

  /**
   * Fetch config for a platform, using cache to avoid duplicate calls.
   * IMPORTANT: only caches successful (non-null) results.
   * If the previous fetch returned null (config not saved yet), re-fetches every time
   * so the user can save credentials in Settings and come back without a full page reload.
   */
  const getOrFetchConfig = async (id) => {
    if (configCache.current[id] != null) return configCache.current[id];
    const cfg = await fetchPlatformConfig(id);
    if (cfg != null) configCache.current[id] = cfg; // only cache valid responses
    return cfg;
  };

  /**
   * Called when a platform card is clicked.
   * Fetches config ONLY for the selected platform (lazy loading).
   */
  const togglePlatform = async (id) => {
    const isSelecting = !selected.includes(id);
    setSelected((prev) =>
      isSelecting ? [...prev, id] : prev.filter((x) => x !== id),
    );
    setMissingPlatforms([]);
    setResults(null);

    if (isSelecting) {
      setLoadingPlatform(id);
      const cfg = await getOrFetchConfig(id);
      const isValid = isValidConfig(id, cfg);
      if (isMounted.current) {
        setConfigStatus((prev) => ({ ...prev, [id]: isValid }));
        setLoadingPlatform(null);
        if (!isValid) {
          // Unselect platform and show login modal immediately
          setSelected((prev) => prev.filter((x) => x !== id));
          openLoginPopup(id);
        }
      }
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setMediaFile(f);
    setMediaType(f.type);
    setMediaPreviewUrl(URL.createObjectURL(f));
    setMediaUrl("");
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setMediaUrl(url);
    setMediaPreviewUrl(url);
    setMediaFile(null);
    setMediaType("");
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaUrl("");
    setMediaPreviewUrl("");
    setMediaType("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePublish = async (e) => {
    // Prevent any parent <form> from triggering a page reload
    if (e && e.preventDefault) e.preventDefault();

    // ── Pre-publish diagnostics ──────────────────────────────

    if (!message.trim() && !mediaFile && !mediaUrl) {
      setMessageErr(true);
      showToast("Please enter a message or select media to post.", "error");
      return;
    }
    if (selected.length === 0) {
      showToast("Please select at least one platform.", "error");
      return;
    }

    // Re-validate configs at publish time using cached data where possible
    const missing = selected.filter((p) => !configStatus[p]);
    if (missing.length > 0) {
      setMissingPlatforms(missing);
      showToast(
        `Please connect: ${missing.join(", ")} before publishing.`,
        "error",
      );
      return;
    }

    setPublishing(true);
    setResults(null);
    setMissingPlatforms([]);
    let processedFile = mediaFile;
    if (mediaFile) {
      try {
        const fd = new FormData();
        fd.append("file", mediaFile);
        const uploadRes = await apiClient.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (uploadRes.data) {
          const generatedFileName = uploadRes.data;
          processedFile = new File([mediaFile], generatedFileName, {
            type: mediaFile.type,
          });
        }
      } catch (err) {
        console.error("[Publish] Pre-upload failed, using original file:", err);
      }
    }

    const res = {};

    // Parallel publishing using Promise.allSettled
    const publishResults = await Promise.allSettled(
      selected.map(async (platformId) => {
        // Use cached config to avoid redundant API calls during publish
        const cfg = await getOrFetchConfig(platformId);
        const c = getConfigObj(cfg);

        if (platformId === "TELEGRAM") {
          await publishToTelegram(message, processedFile, mediaUrl, mediaType);
          return { platformId, ok: true, msg: "Published successfully to Telegram!" };
        } else if (platformId === "FACEBOOK") {
          const facebookRecordId = c?.id;
          if (!c) {
            throw new Error(
              "Facebook configuration object is null. Please go to Settings and re-save your credentials."
            );
          }
          if (!facebookRecordId) {
            throw new Error(
              `Facebook record ID (DB UUID) is missing. Got config: ${JSON.stringify(c)}. ` +
              "Please go to Settings and re-save your Facebook credentials."
            );
          }
          await publishToFacebook(
            facebookRecordId,
            message,
            processedFile,
            mediaUrl,
            mediaType,
          );
          return { platformId, ok: true, msg: `Published to Facebook Page!` };
        } else if (platformId === "INSTAGRAM") {
          const igId = c?.instagramBusinessAccountId || c?.instagramAccountId;
          if (!igId)
            throw new Error(
              "Instagram Business Account ID not found. Please reconnect in Settings."
            );
          if (!processedFile && !mediaUrl)
            throw new Error(
              "Instagram requires a media file or URL to publish."
            );
          await publishToInstagram(
            igId,
            message,
            processedFile,
            mediaUrl,
            mediaType,
          );
          return { platformId, ok: true, msg: "Published successfully to Instagram!" };
        } else if (platformId === "LINKEDIN") {
          return {
            platformId,
            ok: false,
            msg: "LinkedIn post publishing is not yet implemented in the backend.",
          };
        }
      })
    );

    publishResults.forEach((r, i) => {
      const platformId = selected[i];
      if (r.status === "fulfilled") {
        res[platformId] = r.value;
      } else {
        const errData = r.reason?.response?.data;
        const errMsg =
          (typeof errData === "string" && errData) ||
          errData?.message ||
          errData?.error?.message ||
          r.reason?.message ||
          "Unknown error";
        console.error(`[Publish] Failed for ${platformId}:`, r.reason);
        res[platformId] = {
          ok: false,
          msg: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
        };
        const fbErrCode =
          r.reason?.response?.data?.error?.code || r.reason?.response?.data?.errorCode;
        if (
          platformId === "FACEBOOK" &&
          (fbErrCode === 190 || fbErrCode === "190")
        ) {
          // Clear cached config and update status
          delete configCache.current["FACEBOOK"];
          setConfigStatus((prev) => ({ ...prev, FACEBOOK: false }));
        }
      }
    });

    if (isMounted.current) {
      setResults(res);
      setPublishing(false);
      const allOk = Object.values(res).every((r) => r.ok);
      const anyOk = Object.values(res).some((r) => r.ok);
      if (allOk && selected.length > 1) {
        showToast(
          "Post published successfully on all selected platforms.",
          "success",
        );
      } else if (allOk && selected.length === 1) {
        showToast(
          `Successfully posted to ${PLATFORMS.find((p) => p.id === selected[0]).label}.`,
          "success",
        );
      } else if (anyOk) {
        showToast("Partially published — see results below.", "success");
      } else {
        showToast("❌ All publish attempts failed.", "error");
      }

      if (anyOk) {
        // Prepend new published post to Recent Posts list
        const newPost = {
          id: Date.now(),
          message: message,
          time: "Just now",
          status: allOk ? "Published" : "Partially Published",
          platforms: selected,
        };
        setRecentPosts((prev) => [newPost, ...prev]);

        // Increment analytics count
        setAnalyticsData((prev) => ({
          ...prev,
          postsCount: prev.postsCount + 1,
          reachCount: prev.reachCount + Math.floor(Math.random() * 500) + 100,
        }));
      }
    }
  };

  const isImage =
    mediaType?.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(mediaUrl);
  const isVideo =
    mediaType?.startsWith("video/") || /\.(mp4|mov|avi|webm)$/i.test(mediaUrl);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css?family=Nunito:200&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Muli:200&display=swap"
        rel="stylesheet"
      />
      <div className="con1 d-flex">
        <div className="subcon1" style={handleclickonbutton1(hamburger)}>
          <MainSidebar hamburger={hamburger} sethamburger={sethamburger} />
        </div>
        <div className="subcon2" style={handleclickonbutton(hamburger)}>
          <UserNavbar
            hamburger={hamburger}
            sethamburger={sethamburger}
            handelRightMenuShow={handelRightMenuShow}
            handelShow={handelShow}
            isSearchShow={isSearchShow}
            handleScreenEnter={handleScreenEnter}
            handleScreenExit={handleScreenExit}
            fullScreen={fullScreen}
          />
          <div
            className="content-wrapper"
            style={{
              padding: "24px",
              background: "#fafbfe",
              minHeight: "calc(100vh - 56px)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <div style={s.inner}>
              {/* Header Section */}
              <div style={s.headerSection}>
                <div>
                  <h1 style={s.headerTitle}>📡 Enteli Social </h1>
                  <p style={s.headerSubtitle}>
                    Create, schedule, and distribute content across multiple
                    social platforms simultaneously.
                  </p>
                </div>
                <div style={s.headerActions}>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      navigate("/crm-dashboard/enteli-social-redesign-posts")
                    }
                  >
                    📋 Show Posts
                  </button>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      navigate("/crm-dashboard/enteli-social-redesign-settings")
                    }
                  >
                    ⚙️ Settings
                  </button>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      showToast("Drafts feature is coming soon!", "info")
                    }
                  >
                    📝 Drafts
                  </button>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      showToast(
                        "Publish History feature is coming soon!",
                        "info",
                      )
                    }
                  >
                    📜 Publish History
                  </button>
                </div>
              </div>

              {/* Main Grid: Left Column (Composer + Platform Select) & Right Column (Widgets) */}
              <div style={s.dashboardGrid}>
                {/* Left Column */}
                <div style={s.leftCol}>
                  {/* Create Post Card */}
                  <div style={s.card}>
                    <div style={s.cardHead}>
                      <span>✏️</span>
                      <span style={s.cardHeadTitle}>Compose New Post</span>
                    </div>
                    <div style={s.cardBody}>
                      <label style={s.label}>
                        Message <span style={s.labelReq}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <textarea
                          style={{
                            ...s.textarea,
                            ...(messageErr ? s.textareaErr : {}),
                          }}
                          placeholder="Share updates, links, images or videos..."
                          value={message}
                          onChange={(e) => {
                            setMessage(e.target.value);
                            setMessageErr(false);
                          }}
                        />
                        <div style={s.charCounter}>
                          {message.length} characters
                        </div>
                      </div>

                      {/* Media Attachments */}
                      <div style={{ marginTop: "20px" }}>
                        <label style={s.label}>Media Attachments</label>
                        <div style={s.mediaToggle}>
                          {[
                            ["none", "🚫 No Media"],
                            ["file", "📁 Upload File"],
                            ["url", "🔗 Paste URL"],
                          ].map(([mode, lbl]) => (
                            <button
                              key={mode}
                              style={s.mediaTabBtn(mediaMode === mode)}
                              onClick={() => {
                                setMediaMode(mode);
                                clearMedia();
                              }}
                            >
                              {lbl}
                            </button>
                          ))}
                        </div>

                        {mediaMode === "file" && (
                          <div
                            style={s.dragDropArea}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const file = e.dataTransfer.files[0];
                              if (file) {
                                setMediaFile(file);
                                setMediaType(file.type);
                                setMediaPreviewUrl(URL.createObjectURL(file));
                                setMediaUrl("");
                              }
                            }}
                            onClick={() => fileRef.current?.click()}
                          >
                            <input
                              ref={fileRef}
                              type="file"
                              style={s.fileInput}
                              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                              onChange={handleFileChange}
                            />
                            <div
                              style={{ fontSize: "2rem", marginBottom: "8px" }}
                            >
                              📤
                            </div>
                            <div>
                              <strong>Drag & Drop file here</strong> or click to
                              upload
                            </div>
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: C.textMuted,
                                marginTop: "4px",
                              }}
                            >
                              Supports images, videos, and documents (PDF, DOCX, TXT, etc.)
                            </div>
                            {mediaFile && (
                              <div
                                style={{
                                  marginTop: "10px",
                                  fontWeight: 600,
                                  color: C.blue,
                                }}
                              >
                                Selected: {mediaFile.name} (
                                {(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                              </div>
                            )}
                          </div>
                        )}

                        {mediaMode === "url" && (
                          <input
                            style={s.urlInput}
                            type="url"
                            placeholder="https://example.com/image.jpg or video.mp4"
                            value={mediaUrl}
                            onChange={handleUrlChange}
                          />
                        )}

                        {/* Preview */}
                        {mediaPreviewUrl && (
                          <div style={s.preview}>
                            {isImage && (
                              <img
                                src={mediaPreviewUrl}
                                alt="Preview"
                                style={{
                                  maxHeight: "180px",
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                }}
                              />
                            )}
                            {isVideo && (
                              <video
                                src={mediaPreviewUrl}
                                style={{
                                  maxHeight: "180px",
                                  maxWidth: "100%",
                                  borderRadius: "8px",
                                }}
                                controls
                              />
                            )}
                            {!isImage && !isVideo && (
                              <span
                                style={{
                                  color: C.textMuted,
                                  fontSize: "0.85rem",
                                  padding: "12px",
                                }}
                              >
                                📎 Media URL entered
                              </span>
                            )}
                            <button
                              onClick={clearMedia}
                              style={s.removeMediaBtn}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Platform Selector Card */}
                  <div style={s.card}>
                    <div style={s.cardHead}>
                      <span>📲</span>
                      <span style={s.cardHeadTitle}>
                        Select Platforms to Publish To
                      </span>
                    </div>
                    <div style={s.cardBody}>
                      <div style={s.platformGrid}>
                        {PLATFORMS.map((p) => {
                          const isSel = selected.includes(p.id);
                          const isConn = configStatus[p.id];
                          const isLoading = loadingPlatform === p.id;
                          const hasChecked = configStatus[p.id] !== undefined;
                          return (
                            <div
                              key={p.id}
                              style={{
                                ...s.platformCard(isSel, p.color, p.bg),
                                opacity: isLoading ? 0.7 : 1,
                              }}
                              onClick={() => !isLoading && togglePlatform(p.id)}
                            >
                              {isSel && !isLoading && (
                                <span style={s.checkMark}>✔</span>
                              )}
                              <span style={s.platformIcon}>{p.icon}</span>
                              <span style={s.platformName(isSel, p.color)}>
                                {p.label}
                              </span>
                              <span style={s.connBadge(isConn)}>
                                {isLoading
                                  ? "⏳ Checking..."
                                  : hasChecked
                                    ? isConn
                                      ? "● Connected"
                                      : "○ Not Connected"
                                    : "○ Click to check"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <p
                        style={{
                          marginTop: "12px",
                          fontSize: "0.8rem",
                          color: C.textMuted,
                        }}
                      >
                        Platforms marked "Not Connected" must be configured in
                        Settings before publishing.
                      </p>
                    </div>
                  </div>

                  {/* Missing Config Warning */}
                  {missingPlatforms.length > 0 && (
                    <div style={s.missingBanner}>
                      <span style={s.missingIcon}>⚠️</span>
                      <div style={s.missingText}>
                        <div style={s.missingTitle}>
                          You are not logged in to the selected platform. Please
                          login first.
                        </div>
                        <button
                          style={{ ...s.settingsLink, marginTop: "8px" }}
                          onClick={() =>
                            navigate(
                              "/crm-dashboard/enteli-social-redesign-settings",
                            )
                          }
                        >
                          Go To Settings
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div
                    style={{ display: "flex", gap: "12px", marginTop: "12px" }}
                  >
                    <button
                      type="button"
                      style={{
                        ...s.publishBtn(publishing || selected.length === 0),
                        flex: 1,
                        marginTop: 0,
                      }}
                      onClick={handlePublish}
                      disabled={publishing || selected.length === 0}
                    >
                      {publishing ? (
                        <>
                          <span
                            style={{
                              display: "inline-block",
                              animation: "spin 1s linear infinite",
                            }}
                          >
                            ⏳
                          </span>{" "}
                          Publishing...
                        </>
                      ) : (
                        <>
                          <span>🚀</span> Publish Post
                          {selected.length > 1
                            ? ` to ${selected.length} Platforms`
                            : selected.length === 1
                              ? ` to ${PLATFORMS.find((p) => p.id === selected[0])?.label}`
                              : ""}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      style={{
                        padding: "14px",
                        background: "#f1f5f9",
                        color: C.text,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: "10px",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        transition: "all 0.2s",
                      }}
                      onClick={saveDraft}
                    >
                      <span>💾</span> Save Draft
                    </button>
                  </div>

                  {/* Results */}
                  {results && (
                    <div style={s.card}>
                      <div style={s.cardHead}>
                        <span style={{ fontSize: "1.2rem" }}>📊</span>
                        <span style={s.cardHeadTitle}>Publish Results</span>
                      </div>
                      <div style={s.cardBody}>
                        <div style={s.resultsWrap}>
                          {Object.entries(results).map(
                            ([platformId, result]) => {
                              const platform = PLATFORMS.find(
                                (p) => p.id === platformId,
                              );
                              return (
                                <div
                                  key={platformId}
                                  style={s.resultRow(result.ok)}
                                >
                                  <span style={s.resultIcon}>
                                    {result.ok ? "✅" : "❌"}
                                  </span>
                                  <div>
                                    <div style={s.resultTitle(result.ok)}>
                                      {platform?.icon} {platform?.label} —{" "}
                                      {result.ok ? "Published!" : "Failed"}
                                    </div>
                                    <div style={s.resultMsg}>{result.msg}</div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column (Widgets) */}
                <div style={s.rightCol}>
                  {/* Analytics Widget */}
                  <div style={s.card}>
                    <div style={s.cardHead}>
                      <span>📊</span>
                      <span style={s.cardHeadTitle}>Publishing Analytics</span>
                    </div>
                    <div style={s.cardBody}>
                      <div style={s.analyticsGrid}>
                        <div style={s.analyticsStat}>
                          <div style={s.statVal}>
                            {analyticsData.postsCount}
                          </div>
                          <div style={s.statLabel}>Posts Published</div>
                          <div style={s.statTrend}>📈 +12% this week</div>
                        </div>
                        <div style={s.analyticsStat}>
                          <div style={s.statVal}>
                            {analyticsData.reachCount.toLocaleString()}
                          </div>
                          <div style={s.statLabel}>Total Reach</div>
                          <div style={s.statTrend}>📈 +8.3% this week</div>
                        </div>
                        <div style={s.analyticsStat}>
                          <div style={s.statVal}>
                            {analyticsData.engagementRate}%
                          </div>
                          <div style={s.statLabel}>Engagement Rate</div>
                          <div style={s.statTrend}>📈 +2.4% this week</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Scheduled Posts Widget */}
                  <div style={s.card}>
                    <div style={s.cardHead}>
                      <span>🕒</span>
                      <span style={s.cardHeadTitle}>Scheduled Posts</span>
                    </div>
                    <div style={s.cardBody}>
                      <div style={s.widgetList}>
                        {scheduledPosts.map((post) => (
                          <div key={post.id} style={s.widgetItem}>
                            <div style={s.widgetItemHeader}>
                              <span style={s.widgetItemTime}>
                                📅 {post.time}
                              </span>
                              <span style={s.widgetItemStatus}>Scheduled</span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.88rem",
                                fontWeight: 600,
                                color: C.text,
                                margin: "6px 0",
                              }}
                            >
                              {post.title}
                            </div>
                            <div style={s.widgetItemFooter}>
                              Platforms:{" "}
                              {post.platforms.map((p) => {
                                const found = PLATFORMS.find(
                                  (pl) => pl.id === p,
                                );
                                return (
                                  <span
                                    key={p}
                                    style={{ marginRight: "6px" }}
                                    title={found?.label}
                                  >
                                    {found?.icon}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Recent Posts Widget */}
                  <div style={s.card}>
                    <div style={s.cardHead}>
                      <span>📜</span>
                      <span style={s.cardHeadTitle}>Recent Posts</span>
                    </div>
                    <div style={s.cardBody}>
                      <div style={s.widgetList}>
                        {recentPosts.map((post) => (
                          <div key={post.id} style={s.widgetItem}>
                            <div style={s.widgetItemHeader}>
                              <span style={s.widgetItemTime}>
                                🕒 {post.time}
                              </span>
                              <span
                                style={s.recentStatusBadge(
                                  post.status === "Published",
                                )}
                              >
                                {post.status}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.88rem",
                                color: C.text,
                                margin: "6px 0",
                                wordBreak: "break-word",
                              }}
                            >
                              {post.message}
                            </div>
                            <div style={s.widgetItemFooter}>
                              Platforms:{" "}
                              {post.platforms.map((p) => {
                                const found = PLATFORMS.find(
                                  (pl) => pl.id === p,
                                );
                                return (
                                  <span
                                    key={p}
                                    style={{ marginRight: "6px" }}
                                    title={found?.label}
                                  >
                                    {found?.icon}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Social Hub: Published Posts & Saved Drafts */}
              <div style={{ ...s.card, marginTop: "24px" }}>
                <div
                  style={{
                    ...s.cardHead,
                    background: "#fafbfe",
                    borderBottom: `2px solid ${C.border}`,
                  }}
                >
                  <span style={s.cardHeadTitle}>
                    📂 Social Hub: Posts & Saved Drafts
                  </span>
                </div>
                <div style={s.cardBody}>
                  {/* Bottom Tab Bar */}
                  <div style={s.bottomTabs}>
                    <button
                      type="button"
                      style={s.bottomTabBtn(activeBottomTab === "drafts")}
                      onClick={() => setActiveBottomTab("drafts")}
                    >
                      📝 Saved Drafts ({draftsList.length})
                    </button>
                    <button
                      type="button"
                      style={s.bottomTabBtn(activeBottomTab === "posts")}
                      onClick={() => setActiveBottomTab("posts")}
                    >
                      📋 Published Posts ({posts.length})
                    </button>
                  </div>

                  {/* ───────────────── DRAFTS TAB ───────────────── */}
                  {activeBottomTab === "drafts" && (
                    <div>
                      {draftsList.length === 0 ? (
                        <div style={s.emptyState}>
                          <span style={{ fontSize: "2.5rem" }}>📝</span>
                          <div style={{ fontWeight: 600, color: C.textSub }}>
                            No saved drafts.
                          </div>
                          <p
                            style={{
                              color: C.textMuted,
                              fontSize: "0.82rem",
                              margin: 0,
                            }}
                          >
                            Compose a post above and click "Save Draft" to store
                            it here.
                          </p>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "16px",
                            marginTop: "10px",
                          }}
                        >
                          {draftsList.map((draft) => (
                            <div
                              key={draft.id}
                              style={{
                                background: "#ffffff",
                                border: `1.5px solid ${C.border}`,
                                borderRadius: "10px",
                                padding: "16px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                minHeight: "180px",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: C.textMuted,
                                      fontWeight: 600,
                                    }}
                                  >
                                    📅{" "}
                                    {new Date(draft.createdAt).toLocaleString(
                                      undefined,
                                      {
                                        dateStyle: "short",
                                        timeStyle: "short",
                                      },
                                    )}
                                  </span>
                                  <div style={{ display: "flex", gap: "4px" }}>
                                    {draft.platforms.map((platId) => {
                                      const found = PLATFORMS.find(
                                        (pl) => pl.id === platId,
                                      );
                                      return (
                                        <span
                                          key={platId}
                                          style={{ fontSize: "1rem" }}
                                          title={found?.label || platId}
                                        >
                                          {found?.icon || "📲"}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                                <p
                                  style={{
                                    fontSize: "0.9rem",
                                    color: C.text,
                                    margin: "0 0 12px 0",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {draft.message}
                                </p>
                                {draft.mediaUrl && (
                                  <div
                                    style={{
                                      fontSize: "0.72rem",
                                      color: C.blue,
                                      textDecoration: "underline",
                                      marginBottom: "10px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    📎 Media URL:{" "}
                                    {draft.mediaUrl.substring(0, 30)}...
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: "8px",
                                  borderTop: `1px solid ${C.border}`,
                                  paddingTop: "10px",
                                  marginTop: "10px",
                                }}
                              >
                                <button
                                  type="button"
                                  style={s.btnDelete}
                                  onClick={() => handleDeleteDraft(draft.id)}
                                >
                                  🗑️ Delete
                                </button>
                                <button
                                  type="button"
                                  style={s.btnEdit}
                                  onClick={() => handleEditDraft(draft)}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  type="button"
                                  style={s.btnPublish}
                                  onClick={() => handlePublishDraft(draft)}
                                >
                                  🚀 Publish
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ───────────────── PUBLISHED POSTS TAB ───────────────── */}
                  {activeBottomTab === "posts" && (
                    <div>
                      {/* Filter Bar */}
                      <div style={s.filterBar}>
                        <div style={s.filterField}>
                          <span style={s.filterLabel}>Search</span>
                          <input
                            style={s.input}
                            type="text"
                            placeholder="Search by ID or message..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>

                        <div style={s.filterField}>
                          <span style={s.filterLabel}>Platform</span>
                          <select
                            style={s.select}
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                          >
                            <option value="all">All Platforms</option>
                            <option value="telegram">✈️ Telegram</option>
                            <option value="facebook">📘 Facebook</option>
                            <option value="instagram" disabled>
                              📸 Instagram (Coming Soon)
                            </option>
                            <option value="linkedin" disabled>
                              💼 LinkedIn (Coming Soon)
                            </option>
                          </select>
                        </div>

                        <div style={s.filterField}>
                          <span style={s.filterLabel}>Post Type</span>
                          <select
                            style={s.select}
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                          >
                            <option value="all">All Types</option>
                            <option value="text">Text</option>
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </div>

                        <div style={s.filterField}>
                          <span style={s.filterLabel}>From Date</span>
                          <input
                            style={s.input}
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                          />
                        </div>

                        <div style={s.filterField}>
                          <span style={s.filterLabel}>To Date</span>
                          <input
                            style={s.input}
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                          />
                        </div>
                      </div>

                      {loadingPosts ? (
                        <div style={s.emptyState}>
                          <span
                            style={{
                              fontSize: "2rem",
                              animation: "spin 1s linear infinite",
                              display: "inline-block",
                            }}
                          >
                            ⏳
                          </span>
                          <div>Loading published posts...</div>
                        </div>
                      ) : getFilteredPosts().length === 0 ? (
                        <div style={s.emptyState}>
                          <span style={{ fontSize: "2.5rem" }}>📭</span>
                          <div>No published posts found.</div>
                        </div>
                      ) : (
                        <div style={s.tableWrap}>
                          <table style={s.table}>
                            <thead>
                              <tr>
                                <th style={s.th}>Post ID</th>
                                <th style={s.th}>Platform</th>
                                <th style={s.th}>Type</th>
                                <th style={s.th}>Message</th>
                                <th style={s.th}>Published Date</th>
                                <th style={s.th}>Status</th>
                                <th style={s.th}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getFilteredPosts()
                                .slice((currentPage - 1) * 5, currentPage * 5)
                                .map((post, idx) => {
                                  const pf = PLATFORMS.find(
                                    (p) => p.id === post.platform.toUpperCase(),
                                  ) || {
                                    label: post.platform,
                                    icon: "📲",
                                    color: "#64748b",
                                  };
                                  return (
                                    <tr
                                      key={`${post.id}-${idx}`}
                                      style={s.trHover}
                                    >
                                      <td style={s.td}>
                                        <span
                                          style={{
                                            fontFamily: "monospace",
                                            fontWeight: 600,
                                            fontSize: "0.8rem",
                                            color: C.textSub,
                                          }}
                                        >
                                          {post.id
                                            ? post.id
                                              .toString()
                                              .substring(0, 15) +
                                            (post.id.toString().length > 15
                                              ? "..."
                                              : "")
                                            : "N/A"}
                                        </span>
                                      </td>
                                      <td style={s.td}>
                                        <span style={s.platformBadge(pf.color)}>
                                          <span>{pf.icon}</span>
                                          <span>{pf.label}</span>
                                        </span>
                                      </td>
                                      <td style={s.td}>
                                        <span
                                          style={s.typeBadge(post.postType)}
                                        >
                                          {post.postType}
                                        </span>
                                      </td>
                                      <td style={s.td}>
                                        <div
                                          style={{
                                            maxWidth: "300px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontSize: "0.88rem",
                                          }}
                                          title={post.message}
                                        >
                                          {post.message}
                                        </div>
                                        {post.mediaUrl && (
                                          <div
                                            style={{
                                              fontSize: "0.72rem",
                                              color: C.blue,
                                              marginTop: "4px",
                                              textDecoration: "underline",
                                            }}
                                          >
                                            📎 Media Attached
                                          </div>
                                        )}
                                      </td>
                                      <td style={s.td}>
                                        <span
                                          style={{
                                            fontSize: "0.8rem",
                                            color: C.textSub,
                                          }}
                                        >
                                          {post.publishedDate
                                            ? new Date(
                                              post.publishedDate,
                                            ).toLocaleString()
                                            : "Date Unknown"}
                                        </span>
                                      </td>
                                      <td style={s.td}>
                                        <span style={s.statusBadge}>
                                          Published
                                        </span>
                                      </td>
                                      <td style={s.td}>
                                        <button
                                          type="button"
                                          style={s.btnView}
                                          onClick={() =>
                                            handleOpenViewModal(
                                              post.platform,
                                              post.id,
                                            )
                                          }
                                        >
                                          👁️ View
                                        </button>
                                        <button
                                          type="button"
                                          style={s.btnDelete}
                                          onClick={() =>
                                            handleConfirmDeletePost(post)
                                          }
                                        >
                                          🗑️ Delete
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>

                          {/* Pagination */}
                          {Math.ceil(getFilteredPosts().length / 5) > 1 && (
                            <div style={s.pagination}>
                              <span style={s.pagInfo}>
                                Showing {(currentPage - 1) * 5 + 1} to{" "}
                                {Math.min(
                                  currentPage * 5,
                                  getFilteredPosts().length,
                                )}{" "}
                                of {getFilteredPosts().length} posts
                              </span>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  type="button"
                                  style={s.pagBtn(currentPage === 1)}
                                  onClick={() =>
                                    currentPage > 1 &&
                                    setCurrentPage(currentPage - 1)
                                  }
                                  disabled={currentPage === 1}
                                >
                                  ◀ Previous
                                </button>
                                <button
                                  type="button"
                                  style={s.pagBtn(
                                    currentPage ===
                                    Math.ceil(getFilteredPosts().length / 5),
                                  )}
                                  onClick={() =>
                                    currentPage <
                                    Math.ceil(
                                      getFilteredPosts().length / 5,
                                    ) && setCurrentPage(currentPage + 1)
                                  }
                                  disabled={
                                    currentPage ===
                                    Math.ceil(getFilteredPosts().length / 5)
                                  }
                                >
                                  Next ▶
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <div style={s.modalOverlay}>
          <div style={s.modalCard}>
            <div style={s.modalHead}>
              <h3 style={s.modalTitle}>🔐 Connection Setup Required</h3>
              <button
                style={s.modalClose}
                onClick={() => setShowLoginPopup(false)}
              >
                ✕
              </button>
            </div>
            <div style={s.modalBody}>
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <span style={{ fontSize: "3.5rem" }}>🔑</span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  color: C.text,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Account Config Required
              </p>
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "0.9rem",
                  color: C.textSub,
                  lineHeight: 1.6,
                  textAlign: "center",
                }}
              >
                You are not logged in or configured for{" "}
                <strong>
                  {PLATFORMS.find((p) => p.id === loginPopupPlatform)?.label ||
                    loginPopupPlatform}
                </strong>
                .
              </p>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "0.85rem",
                  color: C.textSub,
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                Please redirect to Settings to connect your social media page
                first.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={{ ...s.testBtn, margin: 0 }}
                onClick={() => setShowLoginPopup(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...s.saveBtn, margin: 0 }}
                onClick={() => {
                  setShowLoginPopup(false);
                  navigate("/crm-dashboard/enteli-social-redesign-settings");
                }}
              >
                ⚙️ Go To Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Published Post details Modal */}
      {showViewModal && (
        <div style={s.modalOverlay}>
          <div
            style={{
              background: C.white,
              borderRadius: "16px",
              width: "90%",
              maxWidth: "650px",
              boxShadow: "0 20px 40px rgba(30, 58, 95, 0.15)",
              overflow: "hidden",
              border: `1px solid ${C.border}`,
              display: "flex",
              flexDirection: "column",
              maxHeight: "90vh",
            }}
          >
            <div style={s.modalHead}>
              <h3 style={s.modalTitle}>🔍 Post Detailed Audit Log</h3>
              <button
                style={s.modalClose}
                onClick={() => setShowViewModal(false)}
              >
                ✕
              </button>
            </div>
            <div style={s.modalBody}>
              {fetchingDetail ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <span
                    style={{
                      fontSize: "2.5rem",
                      animation: "spin 1s linear infinite",
                      display: "inline-block",
                    }}
                  >
                    ⏳
                  </span>
                  <div style={{ fontWeight: 600, color: C.textSub }}>
                    Fetching post details...
                  </div>
                </div>
              ) : activePost ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  <div style={s.detailRow}>
                    <span style={s.detailLabel}>Post ID</span>
                    <span
                      style={{
                        ...s.detailValue,
                        fontFamily: "monospace",
                        fontWeight: 600,
                      }}
                    >
                      {activePost.id}
                    </span>
                  </div>
                  <div style={s.detailRow}>
                    <span style={s.detailLabel}>Platform</span>
                    <span style={s.detailValue}>
                      {PLATFORMS.find(
                        (p) => p.id === activePost.platform.toUpperCase(),
                      )?.label || activePost.platform}
                    </span>
                  </div>
                  <div style={s.detailRow}>
                    <span style={s.detailLabel}>Post Type</span>
                    <span style={s.detailValue}>{activePost.postType}</span>
                  </div>
                  <div style={s.detailRow}>
                    <span style={s.detailLabel}>Published Date</span>
                    <span style={s.detailValue}>
                      {activePost.publishedDate
                        ? new Date(activePost.publishedDate).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                  <div
                    style={{
                      borderTop: `1px solid ${C.border}`,
                      paddingTop: "12px",
                    }}
                  >
                    <span style={s.detailLabel}>Message Content</span>
                    <p
                      style={{
                        whiteSpace: "pre-wrap",
                        color: C.text,
                        fontSize: "0.9rem",
                        margin: "6px 0 0 0",
                        background: "#f8fafc",
                        padding: "12px",
                        borderRadius: "8px",
                      }}
                    >
                      {activePost.message}
                    </p>
                  </div>
                  {activePost.mediaUrl && (
                    <div
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        paddingTop: "12px",
                      }}
                    >
                      <span style={s.detailLabel}>Attached Media</span>
                      <div style={s.mediaPreview}>
                        <img
                          src={activePost.mediaUrl}
                          alt="Media preview"
                          style={{
                            maxHeight: "180px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.saveBtn}
                onClick={() => setShowViewModal(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div style={s.toast(toast.type)}>{toast.msg}</div>}

      {/* Spin keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
